import { Suspense, useEffect, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { GuideCoach } from '../astra/Guide';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { ShellProvider } from './ShellContext';
import { ErrorBoundary } from '../ui/ErrorBoundary';
import { PageFallback } from './ShellSkeleton';
import { FixtureNotice, useRouteSource } from '../data-source';
import { AnalysisAsOfNotice } from '../../analysis/ContextBar';
import { AgentContextProvider } from '../../agent/AgentContext';
import { AgentPanel } from '../../agent/AgentPanel';
import { AgentPanelBoundary } from '../../agent/AgentPanelBoundary';
import { useT } from '../../i18n';
import { COMMON } from '../../i18n/common';
import { SHELL } from './messages';
import './shell.css';

/** Карты занимают всю ширину: у них нет колонки чтения. */
const WIDE_ROUTES = ['/map', '/data-map'];

/**
 * Экраны кабинета (`.astra`) верстаются на широкой сетке. Список точный, без
 * префиксов: `/cycles` — это широкий хаб агроциклов, а `/cycles/:id` и
 * `/cycles/:id/works/:workId` — экраны наследия с колонкой чтения.
 */
const ASTRA_ROUTES = new Set([
  '/',
  '/season',
  '/district',
  '/cycles',
  '/planning',
  '/analytics',
  '/reports/briefing',
  '/analysis/water',
  '/guide',
]);

export function AppShell() {
  const { pathname } = useLocation();
  const t = useT();
  const route = useRouteSource();
  const wide = WIDE_ROUTES.some((r) => pathname === r || pathname.startsWith(`${r}/`));
  const path = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  const layout = ASTRA_ROUTES.has(path) ? 'astra' : 'legacy';
  // Телефон (≤600 px): меню — выезжающая панель по кнопке в шапке, а не
  // постоянный рельс 56 px (К4, 07.10.2026). Переход закрывает панель.
  const [navOpen, setNavOpen] = useState(false);
  useEffect(() => setNavOpen(false), [pathname]);
  useEffect(() => {
    if (!navOpen) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navOpen]);

  return (
    <ShellProvider>
      {/* Контекст страницы для ИИ-агента (ADR-0010) — на уровне оболочки:
          она не размонтируется при переходах, и беседа живёт через страницы. */}
      <AgentContextProvider>
        <div className="shell">
          <a className="skip-link" href="#content">
            {t(COMMON.skipToContent)}
          </a>
          <Sidebar open={navOpen} />
          {navOpen ? <button type="button" className="shell__backdrop" aria-label={t(SHELL.menuClose)} onClick={() => setNavOpen(false)} /> : null}
          <div className="main">
            <Topbar navOpen={navOpen} onMenu={() => setNavOpen((v) => !v)} />
            <main className={`content ${wide ? 'content--wide' : ''}`} data-layout={layout} id="content">
              <div className="content__inner">
                {/* Пометка источника стоит ЗДЕСЬ, а не внутри экранов: тогда её
                    нельзя забыть на новом экране — она появляется вместе со
                    строкой в таблице источников (`data-source/routes.ts`). */}
                <FixtureNotice route={route} />
                {/* Две оси канона задаются чипом в шапке (`Topbar`), на
                    экранах, где они что-то значат: `as_of` и `include_demo` —
                    параметры запроса к канону, и на экране фикстур эти ручки
                    врали бы. Здесь — только плашка «показан срез на дату»,
                    когда дата задана (agroai-6al.38). Условие читается из той
                    же таблицы источников, что и пометка выше. */}
                {route?.source === 'api' ? <AnalysisAsOfNotice /> : null}
                {/* Ленивые разделы кабинетов подгружаются отдельным чанком.
                    Оболочка при этом остаётся на месте: мигать сайдбаром из-за
                    загрузки одного экрана нельзя.

                    Порядок важен: `ErrorBoundary` ближе к `Outlet`, чем
                    `Suspense`, а не наоборот. React при серверном рендере сам
                    превращает исключение внутри Suspense-границы в её fallback
                    (страховка от падения всей SSR-страницы) — если Suspense
                    окажется снаружи, эта страховка перехватит настоящую ошибку
                    раньше `ErrorBoundary`, и экран «Экран не отрисован» никогда
                    не покажется, а упавший раздел молча отрисуется как
                    «Раздел загружается…». Обратный порядок (границе ошибок —
                    ближе) не мешает `lazy()`: подвешенный импорт бросает
                    промис, а промис границы ошибок не ловят вообще — он всегда
                    долетает до Suspense поверх. */}
                <Suspense fallback={<PageFallback path={path} />}>
                  <ErrorBoundary key={pathname}>
                    <Outlet />
                  </ErrorBoundary>
                </Suspense>
              </div>
            </main>
          </div>
          <GuideCoach />
          <AgentPanelBoundary>
            <AgentPanel />
          </AgentPanelBoundary>
        </div>
      </AgentContextProvider>
    </ShellProvider>
  );
}
